import user from '@/stores/user';
import storage from '@/utils/storage';
import { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';

const AUTH_TOKEN_KEY = 'auth-token';

interface AuthTokenData {
    token: string;
    userId: string;
    expiry: number;
    address?: string;
}

export default function useSignature() {
    const { setUserId } = user;
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [authToken, setAuthToken] = useState<string | null>(null);
    const { isConnected, address } = useAccount();

    // 检查token是否有效
    const isTokenValid = (): boolean => {
        const storedData = storage.get(AUTH_TOKEN_KEY) as
            | AuthTokenData
            | undefined;
        if (!storedData || !storedData.token || !storedData.expiry) {
            return false;
        }

        // 检查token是否过期
        const isNotExpired = storedData.expiry > Date.now();

        // 检查地址是否匹配
        let isAddressMatch = true; // 默认为true

        // 只有当存储了地址且当前有连接的地址时，才进行地址比较
        if (storedData.address && address) {
            isAddressMatch =
                storedData.address.toLowerCase() === address.toLowerCase();
        }

        // 只有当token未过期且地址匹配时才返回true
        return isNotExpired && isAddressMatch;
    };

    // 获取token
    const getToken = (): string | null => {
        const storedData = storage.get(AUTH_TOKEN_KEY) as
            | AuthTokenData
            | undefined;
        return storedData && storedData.token ? storedData.token : null;
    };

    // 获取userId
    const getUserId = (): string | null => {
        const storedData = storage.get(AUTH_TOKEN_KEY) as
            | AuthTokenData
            | undefined;
        return storedData && storedData.userId ? storedData.userId : null;
    };

    // 保存token和userId
    const saveTokenAndUserId = (
        token: string,
        expiry: number,
        walletAddress: string,
        userId: string,
    ) => {
        storage.set(AUTH_TOKEN_KEY, {
            token,
            expiry,
            address: walletAddress,
            userId,
        });
        setAuthToken(token);
        setUserId(userId);
    };

    // 处理签名成功
    const handleSignSuccess = (token: string, userId: string) => {
        if (address) {
            saveTokenAndUserId(
                token,
                Date.now() + 7 * 24 * 60 * 60 * 1000 - 60 * 60 * 1000,
                address,
                userId,
            ); // 7天过期
        }
        setShowSignatureModal(false);
    };

    // 显示签名弹窗
    const openSignatureModal = () => {
        setShowSignatureModal(true);
    };

    // 关闭签名弹窗
    const closeSignatureModal = () => {
        setShowSignatureModal(false);
    };

    // 当连接状态或地址变化时检查是否需要签名
    useEffect(() => {
        if (isConnected && address) {
            // 如果已连接钱包，检查是否有有效token
            if (!isTokenValid()) {
                // 如果没有有效token或地址不匹配，打开签名弹窗
                openSignatureModal();
            } else {
                // 如果有有效token，设置token
                setAuthToken(getToken());
                setUserId(getUserId());
            }
        } else {
            // 如果未连接钱包，关闭签名弹窗并清除token
            closeSignatureModal();
            setAuthToken(null);
            setUserId(null);
        }
    }, [isConnected, address]);

    return {
        showSignatureModal,
        authToken,
        openSignatureModal,
        closeSignatureModal,
        handleSignSuccess,
    };
}
